import { Emergency } from '@/models/Emergency/Emergency';
import { EmergencyMessage } from '@/models/Emergency/EmergencyMessage';
import GroupUserStatuses from '@/models/User/GroupUserStatuses';
import { UserStatusPingDTO } from '@/models/User/UserStatusPingDTO';
import { UserStatusPingViewModel } from '@/models/User/UserStatusPingViewModel';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import ConnectionStatus from '@/enums/ConnectionStatus';
import Group from '@/models/Organization/Group';

/**
 * @typedef {{
 *   position: { lat: number, lng: number },
 *   user_auth_id: string,
 *   last_updated: number,
 *   bearing: number,
 *   speed: number,
 *   group_name?: string,
 *   group_color?: string,
 *   isEmergencyCreator?: boolean,
 *   isEmergencyResponder?: boolean
 *   hasLostConnection?: boolean,
 * }} LocationMarker
 */

/**
 * @typedef {Object} LocationSharingStoreState
 * @property {boolean} locationSharingEnabled
 * @property {Array<string>} visibleGroups
 * @property {boolean} showLocationSharingPanel
 * @property {boolean} showGroupSelectionModal
 * @property {Array<LocationMarker>} locationMarkers
 * @property {LocationMarker | null} selectedLocationMarker
 */

/**
 * @typedef {Object} LocationSharingStoreActions
 * @property {(enabled: boolean) => void} setLocationSharingEnabled
 * @property {(groups: Array<string>) => void} setVisibleGroups
 * @property {(show: boolean) => void} setShowLocationSharingPanel
 * @property {(show: boolean) => void} setShowGroupSelectionModal
 * @property {(markers: Array<LocationMarker>) => void} setLocationMarkers
 * @property {(marker: LocationMarker | null) => void} setSelectedLocationMarker
 * @property {() => void} toggleLocationSharing
 * @property {(groupId: string) => void} handleGroupToggle
 * @property {() => void} handleGroupSelectionComplete
 * @property {() => void} handleGroupSelectionCancel
 * @property {(userAuthId: string) => void} setSelectedLocationMarkerByUserId
 * @property {() => void} initializeLocationSharingFromStorage
 * @property {() => void} initializeVisibleGroupsFromStorage
 * @property {(allGroupUserStatuses: import('@/context_providers/group_user_context/OrgGroupUserStatusStore').StatusStoreState, emergencyData: Record<string, {
 *    emergency: Emergency | null;
 *    messages: EmergencyMessage[];
 * }>, groups: Array<Group>) => void} updateLocationMarkers
 */

/** @type {import('zustand').UseBoundStore<import('zustand').StoreApi<LocationSharingStoreState & LocationSharingStoreActions>>} */
export const useLocationSharingStore = create(
  subscribeWithSelector((set, get) => ({
    // State
    locationSharingEnabled: true,
    visibleGroups: [],
    showLocationSharingPanel: true,
    showGroupSelectionModal: false,
    locationMarkers: [],
    selectedLocationMarker: null,

    // Actions
    setLocationSharingEnabled: (enabled) => {
      set({ locationSharingEnabled: enabled });
      try {
        localStorage.setItem('locationSharingEnabled', String(enabled));
      } catch (error) {
        console.error(
          'Error saving location sharing state to localStorage:',
          error,
        );
      }
    },
    setVisibleGroups: (groups) => {
      set({ visibleGroups: groups });
      try {
        localStorage.setItem(
          'locationSharingVisibleGroups',
          JSON.stringify(groups),
        );
      } catch (error) {
        console.error('Error saving groups to localStorage:', error);
      }
    },
    setShowLocationSharingPanel: (show) =>
      set({ showLocationSharingPanel: show }),
    setShowGroupSelectionModal: (show) =>
      set({ showGroupSelectionModal: show }),
    setLocationMarkers: (markers) => set({ locationMarkers: markers }),
    setSelectedLocationMarker: (marker) =>
      set({ selectedLocationMarker: marker }),

    // Complex actions
    toggleLocationSharing: () => {
      const state = get();
      const newState = !state.locationSharingEnabled;
      if (newState) {
        set({ showGroupSelectionModal: true });
      } else {
        set({
          locationSharingEnabled: false,
          showLocationSharingPanel: false,
        });
        try {
          localStorage.setItem('locationSharingEnabled', String(false));
        } catch (error) {
          console.error(
            'Error saving location sharing state to localStorage:',
            error,
          );
        }
      }
    },

    handleGroupToggle: (groupId) => {
      const state = get();
      const newVisibleGroups = state.visibleGroups?.includes(groupId)
        ? state.visibleGroups.filter((id) => id !== groupId)
        : [...(state.visibleGroups || []), groupId];

      set({ visibleGroups: newVisibleGroups });
      try {
        localStorage.setItem(
          'locationSharingVisibleGroups',
          JSON.stringify(newVisibleGroups),
        );
      } catch (error) {
        console.error('Error saving groups to localStorage:', error);
      }
    },

    handleGroupSelectionComplete: () => {
      set({
        locationSharingEnabled: true,
        showLocationSharingPanel: true,
        showGroupSelectionModal: false,
      });
      try {
        localStorage.setItem('locationSharingEnabled', String(true));
      } catch (error) {
        console.error(
          'Error saving location sharing state to localStorage:',
          error,
        );
      }
    },

    handleGroupSelectionCancel: () => {
      set({ showGroupSelectionModal: false });
    },

    setSelectedLocationMarkerByUserId: (userAuthId) => {
      const state = get();
      const marker = state.locationMarkers.find(
        (m) => m.user_auth_id === userAuthId,
      );
      set({ selectedLocationMarker: marker || null });
    },

    // Markers are derived from server-provided allGroupUserStatuses / emergencyData. The
    // general-map visibility rule (paid Ops/Respond subscribers, or active-emergency participants)
    // is enforced HERE at marker-sourcing time rather than by redacting location server-side,
    // because the User Dashboard reads this same allGroupUserStatuses data and needs every
    // user's real location regardless of tier. Mirrors the API's
    // SubscriptionTierService.isLocationVisibleOnGeneralMap.
    updateLocationMarkers: (allGroupUserStatuses, emergencyData, groups) => {
      const state = get();
      const userMarkerMap = new Map(); // Deduplication by user_auth_id

      Object.entries(allGroupUserStatuses).forEach(([_, groupUserStatuses]) => {
        if (!groupUserStatuses?.user_statuses) return;

        Object.entries(groupUserStatuses.user_statuses).forEach(
          ([userAuthId, userStatusPing]) => {
            if (
              !userStatusPing?.location ||
              !userStatusPing.shouldShowPinOnMap()
            ) {
              return;
            }

            const userGroup = groups?.find((group) =>
              group.group_value?.some((user) => user.auth_id === userAuthId),
            );

            const isEmergencyCreator = Object.values(emergencyData || {}).some(
              (element) => {
                const isActive =
                  element.emergency && element.emergency.isActiveEmergency();
                const isCreator =
                  element.emergency.creator_auth_id === userAuthId;
                return isActive && isCreator;
              },
            );

            const isEmergencyResponder = Object.values(
              emergencyData || {},
            ).some((element) => {
              const isActive = element.emergency.isActiveEmergency();
              const isResponder = element.emergency.users.some(
                (user) => user.auth_id === userAuthId,
              );
              return isActive && isResponder;
            });

            // General-map visibility gate: hide the pin entirely for Connect-tier users unless
            // they're a participant of an active emergency (location data itself is untouched).
            if (
              !userStatusPing.isPaidSubscriber() &&
              !isEmergencyCreator &&
              !isEmergencyResponder
            ) {
              return;
            }

            const hasLostConnection = userStatusPing.isDisconnected();
            const isLocationSharingVisible =
              state.locationSharingEnabled &&
              userGroup &&
              state.visibleGroups?.includes(userGroup.group_name);

            // Only create marker if user should be visible
            if (
              hasLostConnection ||
              isEmergencyCreator ||
              isEmergencyResponder ||
              isLocationSharingVisible
            ) {
              const location = {
                lat: userStatusPing.location.latitude,
                lng: userStatusPing.location.longitude,
              };

              const marker = {
                position: location,
                user_auth_id: userAuthId,
                last_updated: userStatusPing.location.timestamp * 1000,
                bearing: userStatusPing.location.degrees_bearing,
                speed: userStatusPing.location.meters_per_second_speed,
                group_name: userGroup?.group_name,
                group_color: userGroup?.color,
                isEmergencyCreator,
                isEmergencyResponder,
                hasLostConnection,
              };
              // This ensures only ONE marker per user
              userMarkerMap.set(userAuthId, marker);
            }
          },
        );
      });
      const newLocationMarkers = Array.from(userMarkerMap.values());
      set({ locationMarkers: newLocationMarkers });
    },

    // Initialize from localStorage
    initializeLocationSharingFromStorage: () => {
      try {
        const savedState = localStorage.getItem('locationSharingEnabled');
        const enabled = savedState === null ? true : savedState === 'true';
        set({ locationSharingEnabled: enabled });
      } catch (error) {
        console.error(
          'Error loading location sharing state from localStorage:',
          error,
        );
        set({ locationSharingEnabled: true });
      }
    },

    initializeVisibleGroupsFromStorage: () => {
      try {
        const savedGroups = localStorage.getItem(
          'locationSharingVisibleGroups',
        );
        const groups = savedGroups ? JSON.parse(savedGroups) : [];
        set({ visibleGroups: groups });
      } catch (error) {
        console.error('Error loading visible groups from localStorage:', error);
        set({ visibleGroups: [] });
      }
    },
  })),
);

// Selector hooks for efficient re-renders
export const useLocationSharingEnabled = () =>
  useLocationSharingStore((state) => state.locationSharingEnabled);
export const useVisibleGroups = () =>
  useLocationSharingStore((state) => state.visibleGroups);
export const useShowLocationSharingPanel = () =>
  useLocationSharingStore((state) => state.showLocationSharingPanel);
export const useShowGroupSelectionModal = () =>
  useLocationSharingStore((state) => state.showGroupSelectionModal);
export const useLocationMarkers = () =>
  useLocationSharingStore((state) => state.locationMarkers);
export const useSelectedLocationMarker = () =>
  useLocationSharingStore((state) => state.selectedLocationMarker);

// Action hooks (no need for useShallow since actions don't change)
export const useLocationSharingActions = () => ({
  setLocationSharingEnabled:
    useLocationSharingStore.getState().setLocationSharingEnabled,
  setVisibleGroups: useLocationSharingStore.getState().setVisibleGroups,
  setShowLocationSharingPanel:
    useLocationSharingStore.getState().setShowLocationSharingPanel,
  setShowGroupSelectionModal:
    useLocationSharingStore.getState().setShowGroupSelectionModal,
  setLocationMarkers: useLocationSharingStore.getState().setLocationMarkers,
  setSelectedLocationMarker:
    useLocationSharingStore.getState().setSelectedLocationMarker,
  toggleLocationSharing:
    useLocationSharingStore.getState().toggleLocationSharing,
  handleGroupToggle: useLocationSharingStore.getState().handleGroupToggle,
  handleGroupSelectionComplete:
    useLocationSharingStore.getState().handleGroupSelectionComplete,
  handleGroupSelectionCancel:
    useLocationSharingStore.getState().handleGroupSelectionCancel,
  setSelectedLocationMarkerByUserId:
    useLocationSharingStore.getState().setSelectedLocationMarkerByUserId,
  initializeLocationSharingFromStorage:
    useLocationSharingStore.getState().initializeLocationSharingFromStorage,
  initializeVisibleGroupsFromStorage:
    useLocationSharingStore.getState().initializeVisibleGroupsFromStorage,
  updateLocationMarkers:
    useLocationSharingStore.getState().updateLocationMarkers,
});
